import { useState, useEffect } from "react";
import { Calendar, Repeat, X, Clock, Info } from "lucide-react";

const FREQUENCIES = [
    { id: "once", label: "Uma vez" },
    { id: "daily", label: "Diário" },
    { id: "custom", label: "Horários personalizados" },
    { id: "interval", label: "A cada X horas" },
    { id: "weekly", label: "Dias da semana" },
];

const HOURS = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0") + ":00"
);

const WEEKDAYS = [
    { id: 1, label: "Segunda" },
    { id: 2, label: "Terça" },
    { id: 3, label: "Quarta" },
    { id: 4, label: "Quinta" },
    { id: 5, label: "Sexta" },
    { id: 6, label: "Sábado" },
    { id: 7, label: "Domingo" },
];

function validateOnceDate(onceDate, onceTime) {
    if (!onceDate || !onceTime) return null;
    const dt = new Date(`${onceDate}T${onceTime}:00-03:00`);
    const now = new Date();
    if (dt <= now) return 'A data e horário devem ser no futuro.';
    const maxDate = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000);
    if (dt > maxDate) return 'Não é possível agendar com mais de 10 dias de antecedência.';
    return null;
}

function canAddHour(selected, hour) {
    if (selected.length === 0) return true;
    const toMin = (h) => parseInt(h.split(":")[0]) * 60;
    const candidate = toMin(hour);
    return selected.every((s) => Math.abs(toMin(s) - candidate) >= 180);
}

function buildSummary(freq, state) {
    const { time, customHours, intervalHours, weekdays, onceDate, onceTime } = state;

    if (freq === "once") {
        if (!onceDate || !onceTime || validateOnceDate(onceDate, onceTime)) return null;
        return `O disparo será enviado uma vez em ${onceDate} às ${onceTime} (horário de Brasília).`;
    }
    if (freq === "daily") {
        if (!time) return null;
        return `A campanha será executada todos os dias às ${time} (horário de Brasília).`;
    }
    if (freq === "custom") {
        if (!customHours.length) return null;
        return `A campanha será executada todos os dias nos horários: ${customHours.join(", ")} (horário de Brasília).`;
    }
    if (freq === "interval") {
        if (!intervalHours) return null;
        return `A campanha será executada a cada ${intervalHours}h (horário de Brasília).`;
    }
    if (freq === "weekly") {
        if (!weekdays.length || !time) return null;
        const dayNames = weekdays
            .map((d) => WEEKDAYS.find((w) => w.id === d)?.label)
            .filter(Boolean)
            .join(", ");
        return `A campanha será executada às ${time} nos dias: ${dayNames} (horário de Brasília).`;
    }
    return null;
}

function isReady(freq, state) {
    const { time, customHours, intervalHours, weekdays, onceDate, onceTime } = state;
    if (freq === "once") return !!(onceDate && onceTime) && validateOnceDate(onceDate, onceTime) === null;
    if (freq === "daily") return !!time;
    if (freq === "custom") return customHours.length > 0;
    if (freq === "interval") return !!(intervalHours && intervalHours >= 3 && intervalHours <= 24);
    if (freq === "weekly") return weekdays.length > 0 && !!time;
    return false;
}

export default function DisparoScheduleModal({ open, disparo, onClose, onSchedule }) {
    const [freq, setFreq] = useState("daily");
    const [state, setState] = useState({
        time: "21:00",
        customHours: [],
        intervalHours: 3,
        weekdays: [],
        onceDate: "",
        onceTime: "21:00",
    });

    useEffect(() => {
        if (open) {
            setFreq("daily");
            setState({ time: "21:00", customHours: [], intervalHours: 3, weekdays: [], onceDate: "", onceTime: "21:00" });
        }
    }, [open]);

    if (!open) return null;

    const toggleCustomHour = (hour) => {
        setState((prev) => {
            const already = prev.customHours.includes(hour);
            if (already) return { ...prev, customHours: prev.customHours.filter((h) => h !== hour) };
            if (!canAddHour(prev.customHours, hour)) return prev;
            return { ...prev, customHours: [...prev.customHours, hour].sort() };
        });
    };

    const toggleWeekday = (id) => {
        setState((prev) => ({
            ...prev,
            weekdays: prev.weekdays.includes(id)
                ? prev.weekdays.filter((d) => d !== id)
                : [...prev.weekdays, id],
        }));
    };

    const summary = buildSummary(freq, state);
    const ready = isReady(freq, state);

    const handleConfirm = () => {
        if (!ready) return;
        onSchedule({ freq, ...state });
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div
                className="relative w-full max-w-lg rounded-2xl border border-white/[0.09] bg-[#10141a] p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)] text-white"
                style={{ maxHeight: "90vh", overflowY: "auto", colorScheme: "dark" }}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg font-semibold">
                            <Calendar size={20} className="text-[var(--purple)]" />
                            Agendar Disparo
                        </h2>
                        {disparo && (
                            <p className="mt-1 text-sm text-white/70">
                                Agendando: <span className="font-medium text-white">"{disparo.name}"</span>
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1 opacity-70 hover:opacity-100 hover:bg-white/[0.06] transition-all"
                    >
                        <X size={18} className="text-white/60" />
                    </button>
                </div>

                <div className="space-y-5">
                    {/* Frequência */}
                    <div className="space-y-2">
                        <label className="text-sm text-white/70 font-medium">Frequência</label>
                        <div className="grid grid-cols-2 gap-2">
                            {FREQUENCIES.map((f) => (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => setFreq(f.id)}
                                    className={[
                                        "flex items-center justify-center gap-2 p-3 rounded-lg border text-sm cursor-pointer transition-all",
                                        f.id === "weekly" ? "col-span-2" : "",
                                        freq === f.id
                                            ? "border-[var(--purple)] bg-[var(--purple)] text-white"
                                            : "border-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.04]",
                                    ].join(" ")}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Opções por frequência */}
                    {freq === "once" && (
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <label className="text-sm text-white/70">Data</label>
                                <input
                                    type="date"
                                    value={state.onceDate}
                                    onChange={(e) => setState((p) => ({ ...p, onceDate: e.target.value }))}
                                    className="w-full h-12 rounded-xl px-4 bg-transparent border border-white/[0.06] text-white text-sm  focus:border-[var(--purple)] focus:ring-2 focus:ring-[var(--purple)]/30"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm text-white/70">Horário <span className="text-white/45">(horário de Brasília)</span></label>
                                <input
                                    type="time"
                                    value={state.onceTime}
                                    onChange={(e) => setState((p) => ({ ...p, onceTime: e.target.value }))}
                                    className="w-full h-12 rounded-xl px-4 bg-transparent border border-white/[0.06] text-white text-sm  spt-form-bot focus:border-[var(--purple)] focus:ring-2 focus:ring-[var(--purple)]/30"
                                />
                            </div>
                            {state.onceDate && state.onceTime && validateOnceDate(state.onceDate, state.onceTime) && (
                                <p className="text-xs text-red-400">{validateOnceDate(state.onceDate, state.onceTime)}</p>
                            )}
                        </div>
                    )}

                    {(freq === "daily" || freq === "weekly") && (
                        <div className="space-y-1">
                            <label className="text-sm text-white/70">Horário de execução</label>
                            <input
                                type="time"
                                value={state.time}
                                onChange={(e) => setState((p) => ({ ...p, time: e.target.value }))}
                                className="w-full h-12 rounded-xl px-4 bg-transparent border border-white/[0.06] text-white text-sm spt-form-bot focus:border-[var(--purple)] focus:ring-2 focus:ring-[var(--purple)]/30"
                            />
                        </div>
                    )}

                    {freq === "weekly" && (
                        <div className="space-y-2">
                            <label className="text-sm text-white/70">Dias da semana</label>
                            <div className="grid grid-cols-4 gap-1.5">
                                {WEEKDAYS.map((d) => (
                                    <button
                                        key={d.id}
                                        type="button"
                                        onClick={() => toggleWeekday(d.id)}
                                        className={[
                                            "py-1.5 px-2 text-xs rounded-md transition-all cursor-pointer",
                                            state.weekdays.includes(d.id)
                                                ? "bg-[var(--purple)]/20 bg-[var(--purple)] text-white"
                                                : "bg-transparent border border-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.04]",
                                        ].join(" ")}
                                    >
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {freq === "custom" && (
                        <div className="space-y-2">
                            <label className="text-sm text-white/70">
                                Selecione os horários{" "}
                                <span className="text-white/45">(mínimo 3h de intervalo)</span>
                            </label>
                            <div className="grid grid-cols-6 gap-1.5 max-h-[180px] overflow-y-auto p-2 bg-transparent border border-white/[0.06] rounded-lg">
                                {HOURS.map((h) => {
                                    const selected = state.customHours.includes(h);
                                    const blocked = !selected && !canAddHour(state.customHours, h);
                                    return (
                                        <button
                                            key={h}
                                            type="button"
                                            disabled={blocked}
                                            onClick={() => toggleCustomHour(h)}
                                            className={[
                                                "py-1.5 px-2 text-xs rounded-md transition-all cursor-pointer",
                                                selected
                                                    ? "bg-[var(--purple)]/20 bg-[var(--purple)] text-white"
                                                    : blocked
                                                    ? "opacity-30 border border-white/[0.06] text-white/30 cursor-not-allowed"
                                                    : "bg-transparent border border-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.04]",
                                            ].join(" ")}
                                        >
                                            {h}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {freq === "interval" && (
                        <div className="space-y-2">
                            <label className="text-sm text-white/70">
                                Intervalo em horas{" "}
                                <span className="text-white/45">(mínimo 3h, máximo 24h)</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min={3}
                                    max={24}
                                    value={state.intervalHours}
                                    onChange={(e) =>
                                        setState((p) => ({ ...p, intervalHours: Number(e.target.value) }))
                                    }
                                    className="w-24 h-12 rounded-xl px-4 bg-transparent border border-white/[0.06] text-white text-sm text-center spt-form-bot focus:border-[var(--purple)] focus:ring-2 focus:ring-[var(--purple)]/30 no-step"
                                />
                                <span className="text-white/70 text-sm">horas</span>
                            </div>
                            {state.intervalHours < 3 && (
                                <p className="text-xs text-red-400">O intervalo mínimo é de 3 horas.</p>
                            )}
                            {state.intervalHours > 24 && (
                                <p className="text-xs text-red-400">O intervalo máximo é de 24 horas.</p>
                            )}
                        </div>
                    )}

                    {/* Banner de resumo */}
                    <div
                        className={[
                            "p-3 rounded-lg  transition-all border border-[var(--purple)] bg-white/[0.04]  ",
                            summary
                        ].join(" ")}
                    >
                        <div className="flex items-start gap-2">
                            {summary ? (
                                <Repeat size={16} className="text-[var(--purple)] mt-0.5 shrink-0" />
                            ) : (
                                <Info size={16} className="text-[var(--purple)] mt-0.5 shrink-0" />
                            )}
                            <p className="text-sm text-white/70">
                                {summary ? (
                                    <span dangerouslySetInnerHTML={{ __html: summary.replace(/às (\S+)/, "às <strong class='text-white'>$1</strong>") }} />
                                ) : (
                                    "Configure as opções acima para ativar o agendamento."
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex gap-2 mt-6 justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-10 px-4 rounded-xl border border-white/[0.06] text-white/60 hover:text-white hover:bg-white/[0.04] text-sm font-medium transition-all cursor-pointer"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        disabled={!ready}
                        onClick={handleConfirm}
                        className="h-10 px-4 rounded-xl text-sm font-medium transition-all flex items-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{
                            background: "linear-gradient(135deg, var(--purple), #7c3aed)",
                            boxShadow: "0 4px 20px rgba(139,92,246,0.3)",
                        }}
                    >
                        <Repeat size={16} />
                        Ativar Recorrência
                    </button>
                </div>
            </div>
        </div>
    );
}
